import routes from '@/config/routes';
import store from "@/config/store";
import Api from "@/api/Api";
import {CustomerActions} from "@/config/store/customer/actions";
import {UiMutations} from "@/config/store/ui/mutations";
import VueRouter, {RouteConfig} from 'vue-router';
import i18n from "@/config/i18n";
import ChildView from "@/layouts/ChildView.vue";
import { updateHeadMetadata } from '@/utils/headMetadata';
import { isEqual } from "lodash"
import {updateHreflangTags} from "@/utils/Hreflang";
import { removeTrailingSlash } from '@/utils/Paths';
import { RootCategory } from '@/models/category';

const defaultRoutes: RouteConfig[] = [
    {
        path: "/",
        name: "DefaultHome",
        component: () => import("@/pages/Home.vue")
    },
    {
        path: "/:catalog/:locale",
        component: ChildView,
        children: routes
    },
    {
        path: "/404",
        name: "Notfound",
        component: () => import(  "@/pages/Errors/NotFound.vue"),
    },
    {
        path: "*",
        redirect: { name: "Notfound" },
    }
];

const beforeEnterLocalizedRoute = async (to, from, next) => {
    // DEFINITIONS
    const getCatalogPathFromRoutePath = function(routePath: string) {
        const routeFirstLevelMatches = routePath.match(/^\/([^/]+)/);
        if (!routeFirstLevelMatches) {
            return null;
        }

        return routeFirstLevelMatches[routeFirstLevelMatches.length - 1];
    };

    const getLocaleFromRoutePath = (routePath: string) => {
        const routeSecondLevelMatches = routePath.match(/^\/[^/]+\/([^/]+)/);
        if (!routeSecondLevelMatches) {
            return null;
        }

        return routeSecondLevelMatches[routeSecondLevelMatches.length - 1];
    }

    // INITIALIZATIONS
    const availableCatalogs: RootCategory[] = store.getters.getRootCategories || await store.dispatch("getRootCategories");
    const availableCatalogPaths = availableCatalogs.map(catalog => catalog.path);

    const sessionCatalogPath = (await store.getters.getRootCategory)?.path;
    const fallbackCatalogPath = sessionCatalogPath || availableCatalogPaths[0];

    const fallbackLocale = from.params.locale
      || i18n.locale
      || i18n.fallbackLocale.toString();

    // LOGIC
    if (to.name === "DefaultHome") {
        const sessionCatalogPath = store.getters.getRootCategory?.path;
        const sessionLocale = i18n.locale;

        if (sessionCatalogPath && sessionLocale) {
            return next(
              {
                  name: "Home",
                  params: {
                      catalog: removeTrailingSlash(sessionCatalogPath),
                      locale: sessionLocale
                  },
                  replace: true
              },
            );
        }

        await store.dispatch('openRootCategoryModal');

        return next();
    }

    const toCatalogPath = to.params.catalog
      || getCatalogPathFromRoutePath(to.redirectedFrom || to.path)
      || getCatalogPathFromRoutePath(from.path);
    const toLocale = to.params.locale
      || getLocaleFromRoutePath(to.redirectedFrom || to.path)
      || getLocaleFromRoutePath(from.path);

    if (!to.redirectedFrom && to.name === "Notfound" && (!to.params.catalog || !to.params.locale)) {
        return next({
            name: "Notfound",
            params: {
                catalog: removeTrailingSlash(fallbackCatalogPath),
                locale: fallbackLocale
            }
        });
    }

    const isCatalogPathOk = availableCatalogPaths
      .map(availableCatalogPath => removeTrailingSlash(availableCatalogPath))
      .includes(toCatalogPath);
    const isLocaleOk = i18n.availableLocales.includes(toLocale);
    if (!isCatalogPathOk || !isLocaleOk) {
        const requestedCatalogPath = isCatalogPathOk ? toCatalogPath : fallbackCatalogPath;
        const requestedLocale = isLocaleOk ? toLocale : fallbackLocale;

        const isRedirection = !!to.redirectedFrom;
        const requestedRoutePath = isRedirection && router.resolve(to.redirectedFrom).route.name === "Notfound" ? to.redirectedFrom : to.fullPath;

        // Try to prepend fallback values
        const fallbackPath = `/${removeTrailingSlash(requestedCatalogPath)}/${requestedLocale}${requestedRoutePath}`;
        const fallbackLocation = router.resolve(fallbackPath);

        if (fallbackLocation.route.name) {
            return next(fallbackLocation.route);
        }

        i18n.locale = fallbackLocale;
        return next({ name: "DefaultHome" });
    }

    // If the path doesn't match any route
    if (!to.name) {
        // ...go to Notfound page
        return next(router.resolve(
          {
              name: "Notfound",
              params: {
                  catalog: removeTrailingSlash(fallbackCatalogPath),
                  locale: fallbackLocale
              },
          }
        ).route);
    }

    i18n.locale = toLocale;
    localStorage.VueAppLanguage = toLocale;

    if (toCatalogPath !== removeTrailingSlash(sessionCatalogPath)) {
        const newCatalogPath = availableCatalogs.find(c => removeTrailingSlash(c.path) === toCatalogPath)?.path;
        if (newCatalogPath) {
            await store.dispatch("setRootCategoryPath", newCatalogPath);
        }
    }

    if (to.meta.requireAuth) {
        const authRoute = await requireAuth(to);

        if (authRoute) {
            return next(authRoute);
        }
    }

    return next();
};

export async function requireAuth(to) {
    // VSX 2024-07-19
    // Don't trust local data as it may be out of date with server state,
    // We don't want to allow a user to access a page that requires to be authenticated
    // if he actually is not authenticated anymore on server side du to expired token

    //if (!store.getters.isAuthenticated || !store.getters.getCart) {
    await store.dispatch(CustomerActions.getMe);
    //}

    const cart = store.getters.getCart;

    if (store.getters.isAuthenticated) {
        // only paiement result and paiement return may be accessed with an empty cart,
        // as the previous cart was just transformed into a validated order
        if (!cart?.cartItems.length && to.name != "CheckoutPaymentResult" && to.name != "CheckoutPaymentReturn") {
            return {name: "Cart"};
        }
    } else if (to.name != "CheckoutLogin") {
        // If not authenticated and trying to access a page that is not the connection screen itself,
        // redirect to the connection screen
        return {name: "CheckoutLogin"};
    }

    return null;
}

const SCROLL_POS_TOP = {x: 0, y: 0}

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes: defaultRoutes,
    scrollBehavior: function (to, from, savedPosition) {
        if (to.name === from.name) {
            if (to.hash) {
                return {
                    behaviors: "smooth",
                    selector: to.hash,
                };
            } else if (!isEqual(normalizedRouteParams(to), normalizedRouteParams(from))) {
                return SCROLL_POS_TOP
            } else {
                // If params are the same, consider the page did not change and it could just be paging,
                // and in such a case we may not want to scroll to top.
                // If a custom scroll behaviour is needed, it should be handled by the page component itself
                return savedPosition || null;
            }
        }
        if (savedPosition) {
            return savedPosition;
        } else {
            return SCROLL_POS_TOP
        }
    }
});

// VSX 2025-01-28 This is a test to decorate the router instance, in order to automatically inject prefix params to the requested path
// const orkRouterInstance = new Proxy(router, {
//     get(target, prop, receiver) {
//         console.log("ORK ROUTER PROXY", prop)
//         if (prop === "push") {
//             return function(to) {
//                 console.log("ORK ROUTER PUSH", to)
//                 target.push(to)
//             }
//         }
//         return Reflect.get(target, prop, receiver);
//     }
// })

// Object.defineProperty(Vue.prototype, '$orkRouter', {
//     get() {
//       return orkRouterInstance
//     },
//   })

router.beforeEach(async (to, from, next) => {
    if (process.env.VUE_APP_DEBUG_PRINT_ROUTER === "true") {
        console.debug(`START Routing from '${from.path}' to '${to.path}'`, from, to);
    }
    // FIXME VSX 2024-07-19
    // What is this abort thing??? Is it usefull? If so then it needs a comment as it is not trivial
    // The same goes for the scroll position line
    // const category = Api.product.categoryController
    // if (category) {
    //     category.abort()
    // }
    from.meta?.scrollPos && (from.meta.scrollPos.top = document.documentElement.scrollTop)

    if (!store.getters.getRootCategories) {
        await store.dispatch("getRootCategories");
    }
    //

    await store.dispatch("getRootCategory");
    //
    // // only make a remote call if there's a mismatch
    // if (rootCategories.length === 1 && rootCategories[0].path !== rootCategory?.path) {
    //     await store.dispatch('setRootCategoryPath', rootCategories[0].path || '/');
    // }
    // else if (rootCategories?.length && !rootCategory) {
    //     console.debug("GLOBAL GUARD")
    //     // Vérifier si le chemin de la route actuelle est dans le tableau des chemins à protéger
    //     store.dispatch('toggleRootCategoryModal', {open: true, rootCategory: null}).then();
    // }

    const reversedMatchingRoutes = [...to.matched].reverse()
    to.meta ??= {};
    to.meta.layout = reversedMatchingRoutes.find(route => route.meta.layout)?.meta.layout || 'default-layout';
    to.meta.requireAuth = !!reversedMatchingRoutes.find(route => route.meta.requireAuth);

    await beforeEnterLocalizedRoute(to, from, next);
});

router.afterEach((to, from) => {
    if (process.env.VUE_APP_DEBUG_PRINT_ROUTER === "true") {
        console.debug(`DONE Routing from '${from.path}' to '${to.path}'`);
    }

    updateHeadMetadata();
    updateHreflangTags(to);

    if (from.path !== to.path || !!Object.keys(from.query).length && !!Object.keys(to.query).length) {
        Api.marketingEvent.viewPage({
            routeName: to.name || "",
            url: window.location.href,
            path: to.path,
            title: window.document.title,
            referer: from.fullPath
        });
    }
})

// Monitor display size
// I'm placing this here as I'm sure this file will always get loaded and only once
// It is also delayed as at the moment of execution of this part, as the application context is not fully initialized yet,
// and it would trigger an error (store manipulation)
let displaySizeMediaQuery;

setTimeout(() => {
    const rootComputedStyles = getComputedStyle(document.documentElement);
    const mobileMaxWidth = Number(rootComputedStyles.getPropertyValue('--tablet-min').slice(0, -2)) - 1;

    displaySizeMediaQuery = window.matchMedia("(max-width: " + mobileMaxWidth + "px)");
    displaySizeMediaQuery.onchange = e => updateIsMobile(e);
    updateIsMobile(displaySizeMediaQuery);
})

function updateIsMobile(mql) {
    if (process.env.VUE_APP_DEBUG_PRINT_ROUTER === "true") {
        console.debug("updating is mobile", mql)
    }
    store.commit(UiMutations.IS_MOBILE, mql.matches)
}

export default router;

/**
 * This method is used to compensate an inconsistency in route params when using repeated zero-to-many or one-to-many parameters.
 * For instance on category route, the categorySlugs is a repeated params that may contains multiple values depending on the depth of the category.
 * VueRouter will provide the param as an array if it has more than one element and we navigate programmatically.
 * But if accessing the URL directly, VueRouter will provide the parameter as a string.
 * Eg. For a URL https://mywebsite.com/categorie/bricolage/outils/electroportatif
 * We will get in the first case    categorySlugs = ['bricolage', 'outils', 'electroportatif']
 * And in the second case           categorySlugs = 'bricolage/outils/electroportatif'
 * To get around this issue, with use the following function to normalize a route params,
 * in order to compare parameters of two routes and check if they are the same.
 * See https://github.com/vuejs/vue-router/issues/1175
 * @param route route whose params should be normalized
 * @returns a normalized copy of the route params
 */
export function normalizedRouteParams(route) {
    return Object.entries(route.params).reduce((normalizedParams, paramEntry) => {
        normalizedParams[paramEntry[0]] = Array.isArray(paramEntry[1]) ? paramEntry[1].join('/') : paramEntry[1]
        return normalizedParams
    }, {})
}